/**
 * Shared Triage Types
 *
 * Types shared between client and server for encoder triage system.
 */

// ============================================================================
// Encoder Sample Data (from polling source)
// ============================================================================

/** 
 * @description
 * Raw encoder data as received from the polling source - matches prod JSON schema
 * 
 * @property {number} height
 * @property {number} width
 * @property {number} bitrate - Video bitrate in bits/sec
 * @property {string} real_frame_rate - Fractional notation e.g. "30/1"
 * @property {string} displayAspectRatio
 * @property {string[]} dimensions - Available transcode dimensions
 * @property {boolean} inputHasCaptions
 * @property {string} streamName
 * @property {string} thumbnailUrl
 * @property {string} timestamp - ISO 8601 timestamp from encoder
 */
export interface EncoderSampleData {
  height?: number;
  width?: number;
  bitrate?: number;
  real_frame_rate?: string;
  displayAspectRatio?: string;
  dimensions?: string[];
  inputHasCaptions?: boolean;
  streamName?: string;
  thumbnailUrl?: string;
  timestamp?: string;
}

// ============================================================================
// Triage Session
// ============================================================================

export interface TriageSession {
  encoderId: string;
  streamName: string | null;
  startedAt: number;
  lastActivity: number;
  startedBy: string | null;
  notes: string | null;
  /** Unix ms when session was ended (null if still active) */
  endedAt: number | null;
}

export interface TriageSample {
  id: number;
  encoderId: string;
  /** ISO 8601 timestamp from encoder */
  timestamp: string | null;
  /** Unix ms when we captured this sample */
  collectedAt: number;
  bitrate: number | null;
  realFrameRate: string | null;
  width: number | null;
  height: number | null;
  /** Full JSON for detailed analysis */
  rawJson: string | null;
}

// ============================================================================
// SSE Event Types
// ============================================================================

export type SSEEventType =
  | 'connected'
  | 'heartbeat'
  | 'encoder:alert'
  | 'encoder:alert_update'
  | 'encoder:alert_resolved'
  | 'encoder:update'
  | 'encoder:flapping'
  | 'encoder:flapping:resolved'
  | 'triage:started'
  | 'triage:ended';

export interface SSEEvent<T = unknown> {
  type: SSEEventType;
  data: T;
  timestamp: number;
}

// ============================================================================
// SSE Event Payloads
// ============================================================================

export interface SSEConnectedPayload {
  clientId: string;
}

export interface SSEHeartbeatPayload {
  time: number;
}

export type EncoderAlertType =
  | 'offline'
  | 'zero_bitrate'
  | 'bitrate_too_high'
  | 'bitrate_drop'
  | 'bitrate_unstable'
  | 'persistent_bitrate_floor' // Persistent streams only: below minimum floor
  | 'frame_rate_drop'
  | 'resolution_change';

export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface EncoderAlertPayload {
  encoderId: string;
  streamName?: string;
  alertType: EncoderAlertType;
  message: string;
  currentValue?: number;
  expectedValue?: number;
  severity: AlertSeverity;
  /** Unix ms when alert was first fired */
  firedAt?: number;
  /** Unix ms when anomaly condition was last observed */
  lastAnomalyAt?: number;
  /** Stats for context */
  stats?: {
    mean?: number;
    stddev?: number;
    cv?: number;
    sampleCount?: number;
  };
}

export interface EncoderUpdatePayload {
  encoderId: string;
  sample: EncoderSampleData;
}

export interface TriageStartedPayload {
  encoderId: string;
  session: TriageSession;
}

export interface TriageEndedPayload {
  encoderId: string;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface StartTriageRequest {
  notes?: string;
}

export interface StartTriageResponse {
  session: TriageSession;
}

export interface GetTriageSessionResponse {
  session: TriageSession;
}

export interface GetAllTriageSessionsResponse {
  sessions: TriageSession[];
}

export interface GetTriageHistoryResponse {
  session: TriageSession;
  samples: TriageSample[];
}

export interface EndTriageResponse {
  success: boolean;
}

export interface SSEStatsResponse {
  connectedClients: number;
  clientIds: string[];
}

// ============================================================================
// Triage Configuration
// ============================================================================

export const TRIAGE_CONFIG = {
  /** Maximum time a triage session can exist (14 days in ms) */
  MAX_LIFETIME_MS: 14 * 24 * 60 * 60 * 1000,
  /** Auto-expire triage sessions untouched for this duration (7 days in ms) */
  INACTIVITY_EXPIRY_MS: 7 * 24 * 60 * 60 * 1000,
  /** Maximum samples to store per encoder in triage mode */
  MAX_SAMPLES_PER_ENCODER: 600,
  /** How long to retain ended sessions before deletion (24 hours in ms) */
  ENDED_SESSION_RETENTION_MS: 24 * 60 * 60 * 1000,
} as const;

// ============================================================================
// Alert History Configuration
// ============================================================================

export const ALERT_HISTORY_CONFIG = {
  /** How long to keep alert history (12 hours in ms) */
  RETENTION_MS: 12 * 60 * 60 * 1000,
  /** How often to run cleanup (1 hour in ms) */
  CLEANUP_INTERVAL_MS: 60 * 60 * 1000,
} as const;

// ============================================================================
// Alert History Types
// ============================================================================

/** Alert history record from SQLite */
export interface AlertHistoryRecord {
  id: number;
  encoderId: string;
  streamName: string | null;
  alertType: string;
  severity: string;
  message: string | null;
  currentValue: number | null;
  expectedValue: number | null;
  firedAt: number;
}

/** Summary of alerts per encoder */
export interface AlertHistorySummary {
  encoderId: string;
  streamName: string | null;
  alertCount: number;
  lastAlertAt: number;
  alertTypes: string[];
}

// ============================================================================
// Anomaly Detection Configuration
// ============================================================================

export const ANOMALY_CONFIG = {
  /** Minimum samples required before anomaly detection is active */
  MIN_SAMPLES_FOR_BASELINE: 3,

  /** Bitrate thresholds */
  BITRATE_MAX_BYTES: 5_000_000, // 5 MB/s (40 Mbps) - too high
  BITRATE_DROP_THRESHOLD: 0.5, // Alert if bitrate < baseline * 0.5 (50% drop)

  /** Coefficient of Variation threshold for "unstable" */
  CV_UNSTABLE_THRESHOLD: 0.35, // stddev/mean > 35% (raised from 20% to reduce false positives)

  // ============================================================================
  // Persistent Stream (Linear) Relaxed Thresholds
  // VBR-encoded 24/7 channels naturally fluctuate between content tiers
  // (e.g., 1.2 Mbps for static content, 3.5 Mbps for motion content)
  // ============================================================================

  /** CV threshold for persistent streams - allows normal VBR variance */
  PERSISTENT_CV_UNSTABLE_THRESHOLD: 0.75, // stddev/mean > 75% (raised from 65% to reduce false positives)

  /** Absolute minimum bitrate floor for persistent streams (1 Mbps) */
  PERSISTENT_BITRATE_FLOOR: 1_000_000, // Alert if sustained below 1 Mbps

  /** Consecutive samples required for warning alerts */
  CONSECUTIVE_SAMPLES_WARNING: 4, // Raised from 3 to reduce transient false positives

  /** Alert types and their severity/consecutive requirements */
  ALERT_CONFIG: {
    offline: { severity: 'critical' as const, consecutive: 1 },
    zero_bitrate: { severity: 'critical' as const, consecutive: 1 },
    bitrate_too_high: { severity: 'critical' as const, consecutive: 1 },
    bitrate_drop: { severity: 'warning' as const, consecutive: 4 },
    bitrate_unstable: { severity: 'warning' as const, consecutive: 4 },
    persistent_bitrate_floor: { severity: 'warning' as const, consecutive: 5 },
    frame_rate_drop: { severity: 'warning' as const, consecutive: 4 },
    resolution_change: { severity: 'info' as const, consecutive: 1 },
  },
} as const;
